import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { FiAlertCircle } from "react-icons/fi";

const AzureCallbackPage = () => {

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const calledOnce = useRef(false);

  const code = searchParams.get("code");
  const stateParam = searchParams.get("state");
  const errorParam = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  useEffect(() => {
    const handleCallback = async () => {
      if (calledOnce.current) return;
      calledOnce.current = true;
      if (errorParam) {
        setStatus("error");
        setErrorMessage(errorDescription || "Authentication failed. Please try again.");
        return;
      }
      if (!code) {
        setStatus("error");
        setErrorMessage("No authorization code found in the URL.");
        return;
      }
      try {
        const response = await api.handleAzureAuthCallback({ code, state: stateParam });

        if (response && response.data && response.data.token) {
          login(response.data);
          navigate("/participant/dashboard", { replace: true });
        } else {
          throw new Error("Invalid response from server. Missing token.");
        }
      } catch (error) {
        setStatus("error");
        setErrorMessage(error?.response?.data?.message || "Failed to exchange authorization code for token.");
      }
    };

    handleCallback();
  }, [code, stateParam, errorParam, errorDescription, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg border border-gray-100 text-center">
        {status === "loading" && (
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-[#0A3059] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">Authenticating...</h2>
            <p className="mt-2 text-sm text-gray-500">Please wait while we complete your sign in.</p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center">
            <FiAlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign In Failed</h2>
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md w-full mb-6">
              {errorMessage}
            </p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0A3059] hover:bg-[#072445]"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AzureCallbackPage;
